import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import './giftWrap-typedefs.js';
import { getCart } from '@shared/gateways/ajax-api/cart';
import { checkCartForUvInsert } from '@shared/view/gift-wrap/checkCartForUvInsert';
import { giftWrapData } from '../gateway/giftWrapData.js';
import { useUvInsertsContext } from '../uv-inserts/context/UvInsertsContext.tsx';
import { logError } from '../utilities/logging.js';
import { removeGid } from '../utilities/handleGid.js';

// Minimal GiftWrap context skeleton
export const GiftWrapContext = createContext({
  loading: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLoading: (/** @type {boolean} */ _value) => {},
  addToCartLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setAddToCartLoading: (/** @type {boolean} */ _value) => {},
  giftWrapModalOpen: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setGiftWrapModalOpen: (/** @type {boolean} */ _value) => {},
  priceModalOpen: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setPriceModalOpen: (/** @type {boolean} */ _value) => {},
  bagInfoOpen: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setBagInfoOpen: (/** @type {boolean} */ _value) => {},
  addBaseProduct: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setAddBaseProduct: (/** @type {boolean} */ _value) => {},
  wrapOptions: /** @type {import('./giftWrap-typedefs.js').IGiftWrap['wrapOptions']} */ ([]),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setWrapOptions: (/** @type {import('./giftWrap-typedefs.js').IGiftWrap['wrapOptions']} */ _value) => {
    // @TODO: Implement wrap options setter
  },
  personalisationChoices: /** @type {import('./giftWrap-typedefs.js').IGiftWrap['personalisationChoices']} */ ([]),
  setPersonalisationChoices: (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    /** @type {import('./giftWrap-typedefs.js').IGiftWrap['personalisationChoices']} */ _value
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // @TODO: Implement personalisation choices setter
  },
  activeProduct: {
    id: '',
    title: '',
    price: 0
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setActiveProduct: (/** @type {{id: string, title: string, price: number}} */ _value) => {},
  activePersonalisation: /** @type {import('./giftWrap-typedefs.js').IGiftWrap['activePersonalisation']} */ ({
    parentId: '',
    vId_gift: '',
    text: null,
    vId_stamp: null,
    vId_stamp_label: null,
    title: null,
    price: null,
    vendor: null
  }),
  /** @type {(value: import('./giftWrap-typedefs.js').IGiftWrap['activePersonalisation']) => void} */
  setActivePersonalisation: (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    /** @type {import('./giftWrap-typedefs.js').IGiftWrap['activePersonalisation']} */ _value
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // @TODO: Implement active personalisation setter
  },
  giftInfo: /** @type {import('./giftWrap-typedefs.js').IGiftWrap['giftInfo']} */ (null),
  /** @type {(value: any) => void} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setGiftInfo: (/** @type {any} */ _value) => {},
  selectedItems: /** @type {Array<{id: string, title: string, price: number, variantId: string}>} */ ([]),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSelectedItems: (/** @type {Array<{id: string, title: string, price: number, variantId: string}>} */ _value) => {},
  /** @type {(parentId: string, wrapOption: any) => void} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  toggleGiftWrapSelection: (/** @type {string} */ _parentId, /** @type {any} */ _wrapOption) => {},
  /** @type {(parentId: string) => boolean} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isGiftWrapItemSelected: (/** @type {string} */ _parentId) => false,
  calculateGiftWrapTotal: 0,
  calculateTotalPrice: 0,
  /** @type {(parentId: string, updates: any) => void} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updatePersonalisationChoice: (/** @type {string} */ _parentId, /** @type {any} */ _updates) => {},
  /** @type {(parentId: string) => any} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getInputState: (/** @type {string} */ _parentId) => ({}),
  /** @type {(parentId: string, updates: any) => void} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateInputState: (/** @type {string} */ _parentId, /** @type {any} */ _updates) => {},
  /** @type {() => void} */
  resetGiftWrapState: () => {},
  inputStates: {},
  // Visualiser export API stubs for default context value.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerVisualiser: (/** @type {string} */ _faceId, /** @type {any} */ _exporters) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  unregisterVisualiser: (/** @type {string} */ _faceId) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVisualiserExporters: (/** @type {string} */ _faceId) =>
    /** @type {{ getBlob?: () => Promise<Blob | null>, getDataURL?: () => string | null, getText?: () => string | null } | null} */ (
      null
    ),
  exportAllVisualsAsBlobs: async () => /** @type {Record<string, Blob>} */ ({}),
  exportAllVisualsAsDataURLs: () => /** @type {Record<string, string>} */ ({}),
  // Visualiser choices (stateful values like text per face).
  visualiserChoices: /** @type {Record<string, { text?: string }>} */ ({}),
  /** @type {(faceId: string) => ({ text?: string } | null)} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVisualiserChoice: (/** @type {string} */ _faceId) => null,
  /** @type {(faceId: string, updates: { text?: string }) => void} */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setVisualiserChoice: (/** @type {string} */ _faceId, /** @type {{ text?: string }} */ _updates) => {},
  /** Resolved UV inserts from UvInsertsContext (after mount). */
  resolvedUvInserts: /** @type {import('@shared/view/uv-inserts/resolve-uv-inserts').ResolvedUvInserts} */ ({
    configs: [],
    templateIds: {},
    suggestedMessaging: []
  }),
  /** When true, gift wrap skips the UV inserts sub-flow (Made Mine line already has an insert). */
  skipUvInsertsFlow: false
});

/**
 * Provides gift wrap context to children.
 * @param {{ children: React.ReactNode }} props
 * @returns {React.ReactElement}
 */
const emptyResolvedUvInserts = {
  configs: [],
  templateIds: {},
  suggestedMessaging: []
};

export const GiftWrapProvider = ({ children }) => {
  const { resolved: resolvedUvInsertsFromUv, resetUvInsertFlowState, setProductSegment } = useUvInsertsContext();
  const resolvedUvInserts = resolvedUvInsertsFromUv ?? emptyResolvedUvInserts;

  // Ensure loading is defined in this scope (default false)
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(/** @type {boolean} */ (false));
  const [addToCartLoading, setAddToCartLoading] = useState(/** @type {boolean} */ (false));
  const [cartHasUvInsert, setCartHasUvInsert] = useState(/** @type {boolean} */ (false));
  const [giftWrapModalOpen, setGiftWrapModalOpen] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [bagInfoOpen, setBagInfoOpen] = useState(false);
  const [addBaseProduct, setAddBaseProduct] = useState(true);

  const [activeProduct, setActiveProduct] = useState({
    id: '',
    title: '',
    price: 0
  });

  // New state for multiple selected items
  const [selectedItems, setSelectedItems] = useState(
    /** @type {Array<{id: string, title: string, price: number, variantId: string}>} */ ([])
  );

  const [activePersonalisation, setActivePersonalisation] = useState(
    /** @type {import('./giftWrap-typedefs.js').IGiftWrap['activePersonalisation']} */ ({
      parentId: '',
      vId_gift: '',
      text: null,
      vId_stamp: null,
      vId_stamp_label: null,
      title: null,
      price: null,
      vendor: null
    })
  );

  // New states to match typedefs
  const [wrapOptions, setWrapOptions] = useState(
    /** @type {import('./giftWrap-typedefs.js').IGiftWrap['wrapOptions']} */ ([])
  );

  const [personalisationChoices, setPersonalisationChoices] = useState(
    /** @type {import('./giftWrap-typedefs.js').IGiftWrap['personalisationChoices']} */ ([])
  );

  const [giftInfo, setGiftInfo] = useState(
    /** @type {import('./giftWrap-typedefs.js').IGiftWrap['giftInfo']} */ (null)
  );

  // Visualiser choices per faceId (e.g., 'insert.front', 'insert.back').
  const [visualiserChoices, setVisualiserChoices] = useState(/** @type {Record<string, { text?: string }>} */ ({}));

  // Visualiser registry for exporting rendered faces as images.
  // The registry holds exporters keyed by a stable faceId (e.g., 'insert.front', 'card.insideRight').
  const visualiserRegistryRef = useRef(
    /** @type {Map<string, { getBlob?: () => Promise<Blob | null>, getDataURL?: () => string | null, getText?: () => string | null }>} */ (
      new Map()
    )
  );

  /** True after `add_gift_wrap` closed the cart drawer; cleared when reopening after the modal closes. */
  const cartClosedForGiftWrapRef = useRef(false);
  /** Tracks prior `giftWrapModalOpen` to detect true → false without firing on initial mount. */
  const prevGiftWrapModalOpenRef = useRef(false);

  /**
   * Registers a visualiser exporter under a given faceId.
   * @param {string} faceId - Stable identifier for the visual face (e.g., 'insert.front').
   * @param {{ getBlob?: () => Promise<Blob | null>, getDataURL?: () => string | null, getText?: () => string | null }} exporters - Exporter functions.
   * @returns {void}
   */
  const registerVisualiser = useCallback((faceId, exporters) => {
    if (!faceId || typeof faceId !== 'string') return;
    visualiserRegistryRef.current.set(faceId, exporters || {});
  }, []);

  /**
   * Unregisters a visualiser exporter by faceId.
   * @param {string} faceId - The face identifier to remove.
   * @returns {void}
   */
  const unregisterVisualiser = useCallback((faceId) => {
    if (!faceId || typeof faceId !== 'string') return;
    visualiserRegistryRef.current.delete(faceId);
  }, []);

  /**
   * Gets exporters for a specific faceId if registered.
   * @param {string} faceId - The face identifier.
   * @returns {{ getBlob?: () => Promise<Blob | null>, getDataURL?: () => string | null, getText?: () => string | null } | null}
   */
  const getVisualiserExporters = useCallback((faceId) => {
    return visualiserRegistryRef.current.get(faceId) || null;
  }, []);

  /**
   * Exports all registered visuals to Blobs keyed by faceId.
   * @returns {Promise<Record<string, Blob>>}
   */
  const exportAllVisualsAsBlobs = useCallback(async () => {
    const result = /** @type {Record<string, Blob>} */ ({});
    const entries = Array.from(visualiserRegistryRef.current.entries());
    for (const [faceId, exporters] of entries) {
      try {
        if (exporters?.getBlob) {
          const blob = await exporters.getBlob();
          if (blob) {
            result[faceId] = blob;
          }
        }
      } catch {
        // Ignore export failures for individual faces.
      }
    }
    return result;
  }, []);

  /**
   * Exports all registered visuals to Data URLs keyed by faceId.
   * @returns {Record<string, string>}
   */
  const exportAllVisualsAsDataURLs = useCallback(() => {
    const result = /** @type {Record<string, string>} */ ({});
    const entries = Array.from(visualiserRegistryRef.current.entries());
    for (const [faceId, exporters] of entries) {
      try {
        if (exporters?.getDataURL) {
          const url = exporters.getDataURL();
          if (url) {
            result[faceId] = url;
          }
        }
      } catch {
        // Ignore export failures for individual faces.
      }
    }
    return result;
  }, []);

  /**
   * Reads stored visualiser choice by faceId.
   * @param {string} faceId
   * @returns {{ text?: string } | null}
   */
  const getVisualiserChoice = useCallback(
    (faceId) => {
      if (!faceId) return null;
      return visualiserChoices[`${faceId}`] || null;
    },
    [visualiserChoices]
  );

  /**
   * Sets visualiser choice updates for a faceId.
   * @param {string} faceId
   * @param {{ text?: string }} updates
   * @returns {void}
   */
  const setVisualiserChoice = useCallback((faceId, updates) => {
    if (!faceId || !updates) return;
    setVisualiserChoices((prev) => ({
      ...prev,
      [faceId]: { ...(prev[`${faceId}`] || {}), ...(updates || {}) }
    }));
  }, []);

  // Input control states - moved from components to context
  const [inputStates, setInputStates] = useState(
    /** @type {Record<string, {isExpanded: boolean, addPersonalisation: boolean, addWaxStamp: boolean, personalisationText: string, selectedDesign: string, giftMessage: string}>} */ ({})
  );

  // Centralized selection management
  const toggleGiftWrapSelection = useCallback(
    (parentId, wrapOption) => {
      // Check if item is currently selected
      const isCurrentlySelected = personalisationChoices.some((choice) => choice.parentId === parentId);

      setPersonalisationChoices((prev) => {
        const idx = prev.findIndex((choice) => choice.parentId === parentId);

        if (idx === -1) {
          // Add new selection
          // Try multiple sources for variant ID
          let vId_gift = '';
          if (wrapOption.variants && wrapOption.variants.length > 0) {
            vId_gift = wrapOption.variants[0].id || '';
          } else if (wrapOption.variant_id) {
            vId_gift = wrapOption.variant_id;
          } else if (wrapOption.id) {
            vId_gift = wrapOption.id;
          }

          const newChoice = {
            type: wrapOption.type === 'stamp_bag' || wrapOption.type === 'stamp_box' ? 'bag' : wrapOption.type,
            parentId: wrapOption.product_id,
            vId_gift: vId_gift,
            text: null,
            vId_stamp: null,
            vId_stamp_label: null,
            title: wrapOption.title || 'Gift Wrap Item',
            price: String(wrapOption.price || 0),
            vendor: wrapOption.vendor || null
          };
          const newChoices = [...prev, newChoice];

          // Auto-expand the item when it's selected
          setInputStates((prevStates) => ({
            ...prevStates,
            [parentId]: {
              ...(prevStates[`${parentId}`] || {
                isExpanded: false,
                addPersonalisation: false,
                addWaxStamp: false,
                personalisationText: '',
                selectedDesign: '',
                giftMessage: ''
              }),
              isExpanded: true
            }
          }));

          return newChoices;
        } else {
          // Remove existing selection
          const newChoices = prev.filter((_, i) => i !== idx);
          return newChoices;
        }
      });

      // Clear expanded state and active personalisation when deselecting (after state update)
      if (isCurrentlySelected) {
        setInputStates((prev) => ({
          ...prev,
          [parentId]: {
            isExpanded: false,
            addPersonalisation: false,
            addWaxStamp: false,
            personalisationText: '',
            selectedDesign: '',
            giftMessage: ''
          }
        }));

        // Clear active personalisation if it matches the deselected item
        setActivePersonalisation((prev) => {
          if (prev.parentId === parentId) {
            return {
              type: 'bag',
              parentId: '',
              vId_gift: '',
              text: null,
              vId_stamp: null,
              vId_stamp_label: null,
              title: null,
              price: null,
              vendor: null
            };
          }
          return prev;
        });
      }
    },
    [personalisationChoices]
  );

  // Update personalization choices with additional options
  const updatePersonalisationChoice = useCallback((parentId, updates) => {
    setPersonalisationChoices((prev) => {
      const idx = prev.findIndex((choice) => choice.parentId === parentId);
      if (idx !== -1) {
        const updatedChoices = [...prev];
        updatedChoices[`${idx}`] = { ...updatedChoices[`${idx}`], ...updates };
        return updatedChoices;
      }
      return prev;
    });
  }, []);

  // Reset all gift wrap state to default values.
  const resetGiftWrapState = useCallback(() => {
    setPersonalisationChoices([]);
    setActivePersonalisation({
      type: 'bag',
      parentId: '',
      vId_gift: '',
      text: null,
      vId_stamp: null,
      vId_stamp_label: null,
      title: null,
      price: null,
      vendor: null
    });
    setActiveProduct({
      id: '',
      title: '',
      price: 0
    });
    setSelectedItems([]);
    setInputStates({});
    setGiftInfo(null);
    setPriceModalOpen(false);
    setBagInfoOpen(false);
    setAddBaseProduct(true);
    setCartHasUvInsert(false);
    resetUvInsertFlowState();
    // Clear any registered visualisers as state is reset.
    visualiserRegistryRef.current.clear();
  }, [resetUvInsertFlowState]);

  // Input state management functions
  const getInputState = useCallback(
    (parentId) => {
      return (
        inputStates[`${parentId}`] || {
          isExpanded: false,
          addPersonalisation: false,
          addWaxStamp: false,
          personalisationText: '',
          selectedDesign: '',
          giftMessage: ''
        }
      );
    },
    [inputStates]
  );

  const updateInputState = useCallback((parentId, updates) => {
    setInputStates((prev) => {
      const currentState = prev[`${parentId}`] || {
        isExpanded: false,
        addPersonalisation: false,
        addWaxStamp: false,
        personalisationText: '',
        selectedDesign: '',
        giftMessage: ''
      };

      // Only update if there are actual changes
      const hasChanges = Object.keys(updates).some((key) => {
        // @TODO: Add proper type checking for updates
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, security/detect-object-injection
        return currentState[key] !== updates[key];
      });
      if (!hasChanges) {
        return prev;
      }

      return {
        ...prev,
        [parentId]: {
          ...currentState,
          ...updates
        }
      };
    });
  }, []);

  // Check if an item is selected
  const isGiftWrapItemSelected = useCallback(
    (parentId) => {
      const isSelected = personalisationChoices.some((choice) => choice.parentId === parentId);
      return isSelected;
    },
    [personalisationChoices]
  );

  // Calculate total price for selected gift wrap items using useMemo
  const calculateGiftWrapTotal = useMemo(() => {
    let total = 0;

    // Create a map of all variants from wrap options
    const allVariants = new Map();

    // Handle wrap options that have variants array
    wrapOptions.forEach((option) => {
      if (option.variants && Array.isArray(option.variants)) {
        option.variants.forEach((variant) => {
          allVariants.set(variant.id, variant);
        });
      }
      // Handle wrap options that are variants themselves
      if (option.product_id && option.price !== undefined) {
        allVariants.set(option.product_id, option);
      }
    });

    for (const item of personalisationChoices || []) {
      // Base gift wrap item price
      if (item.vId_gift) {
        const variant = allVariants.get(item.vId_gift);
        if (variant && variant.price) {
          total += variant.price;
        }
      }

      // Personalization text price (if text is provided)
      if (item.text && item.text.trim() !== '') {
        // Find personalization variant (usually has "custom text" or "add a name" in title)
        const personalizationVariant = allVariants.get(item.vId_gift);
        if (personalizationVariant && personalizationVariant.title?.toLowerCase().includes('custom text')) {
          total += 5; // Personalization text costs £5
        }
      }

      // Wax stamp price - improved logic to ensure it's always included
      if (item.vId_stamp) {
        const variant = allVariants.get(item.vId_stamp);
        if (variant && variant.price) {
          total += variant.price;
        } else {
          // If variant not found in map, still add the default cost
          // This ensures wax stamp prices are always included in the total
          total += 1; // Default wax stamp cost £1
        }
      }
    }
    return total;
  }, [personalisationChoices, wrapOptions]);

  // Calculate total price including base product and gift wrap
  const calculateTotalPrice = useMemo(() => {
    return (activeProduct.price || 0) + calculateGiftWrapTotal;
  }, [activeProduct.price, calculateGiftWrapTotal]);

  // @ts-expect-error - Shopify is added to window by Shopify's global scripts
  const countryCode = window.Shopify?.country || 'GB';

  const handleCustomEvent = useCallback((e) => {
    setEventData(e.detail);
  }, []);

  useEffect(() => {
    // Respect existing configuration; do not force-enable gift wrap globally.
    document.addEventListener('GIFTWRAP_EVENT', handleCustomEvent);

    return () => {
      document.removeEventListener('GIFTWRAP_EVENT', handleCustomEvent);
    };
  }, [handleCustomEvent]);

  useEffect(() => {
    if (eventData) {
      // @ts-expect-error - eventData has dynamic properties from custom events
      if (eventData.action === 'add_gift_wrap') {
        setAddBaseProduct(false);
        cartClosedForGiftWrapRef.current = true;
        // @ts-expect-error - CartDrawer is added to window by cart-drawer.js
        window.CartDrawer?.close();
      }

      // @ts-expect-error - eventData has dynamic properties from custom events
      if (eventData.action === 'show_gift_wrap_modal' || eventData.action === 'add_gift_wrap') {
        // Ensure product data exists before accessing it.
        // @ts-expect-error - eventData has dynamic properties from custom events
        const product = eventData.product || {};
        // @ts-expect-error - eventData has dynamic properties from custom events
        const variantId = eventData.variantId || '';

        setActiveProduct({
          id: variantId,
          title: product.title || 'Unknown Product',
          price: (product.price || 0) / 100
        });

        // Initialize selectedItems with the current product.
        setSelectedItems([
          {
            id: variantId,
            title: product.title || 'Unknown Product',
            price: (product.price || 0) / 100,
            variantId: variantId
          }
        ]);

        setGiftWrapModalOpen(true);
      }
    }
  }, [eventData]);

  useEffect(() => {
    const wasOpen = prevGiftWrapModalOpenRef.current;
    if (wasOpen && !giftWrapModalOpen && cartClosedForGiftWrapRef.current) {
      cartClosedForGiftWrapRef.current = false;
      // Gift wrap uses a native <dialog showModal> top layer. Opening the cart in the same turn as
      // React flipping the modal closed can race layout, focus, and stacking. Two nested rAFs defer
      // CartDrawer.open until after the next paint so the dialog teardown has run first.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // @ts-expect-error - CartDrawer is added to window by cart-drawer.js
          window.CartDrawer?.open?.();
        });
      });
    }
    prevGiftWrapModalOpenRef.current = giftWrapModalOpen;
  }, [giftWrapModalOpen]);

  useEffect(() => {
    if (!giftWrapModalOpen) {
      setPriceModalOpen(false);
      return;
    }

    setLoading(true);
    //* Get data and set data with main product ID for vendor matching
    // @ts-expect-error - eventData has dynamic properties from custom events
    const mainProductId = eventData?.productId || eventData?.product?.id || eventData?.variantId;

    let isCancelled = false;

    giftWrapData({
      country: countryCode,
      mainProductId: mainProductId
    })
      .then((data) => {
        // Check if the effect was cancelled before updating state.
        if (isCancelled) return;

        // Safely assign returned parts (depending on gateway shape).
        if (data) {
          // Push the wrapped product's market segment to UvInsertsContext so UV insert
          // configs are filtered by segment (empty/unknown → WOMENS via the shared rule).
          setProductSegment(data.mainProductData?.segment ?? null);

          // Check if there are vendor matches (from the event data or the fresh data).
          // @ts-expect-error - eventData has dynamic properties from custom events
          const hasVendorMatches = eventData?.vendorMatching?.hasMatches ?? data.hasVendorMatches;

          if (!hasVendorMatches || !data.wrapOptions || data.wrapOptions.length === 0) {
            // Don't close modal immediately to prevent flickering.
            // Instead, set empty wrap options and let the UI handle the empty state.
            setWrapOptions([]);
            return;
          }

          setWrapOptions(data.wrapOptions);
        }
      })
      .catch((error) => {
        // Only log error if the effect wasn't cancelled.
        if (!isCancelled) {
          logError({
            label: 'GiftWrapContext',
            error,
            message: 'Failed to fetch gift wrap data',
            extraData: { country: countryCode, mainProductId }
          });
        }
      })
      .finally(() => {
        // Only update loading state if the effect wasn't cancelled.
        if (!isCancelled) {
          setLoading(false);
        }
      });

    // Cleanup function to cancel the async operation.
    return () => {
      isCancelled = true;
    };
  }, [
    giftWrapModalOpen,
    countryCode,
    setProductSegment,
    // @ts-expect-error - eventData has dynamic properties from custom events
    eventData?.productId,
    // @ts-expect-error - eventData has dynamic properties from custom events
    eventData?.variantId,
    // @ts-expect-error - eventData has dynamic properties from custom events
    eventData?.product?.id
  ]);

  useEffect(() => {
    if (!giftWrapModalOpen) {
      setCartHasUvInsert(false);
      return;
    }

    const variantIdRaw = activeProduct?.id;
    if (!variantIdRaw) {
      setCartHasUvInsert(false);
      return;
    }

    const normalizedVariantId = removeGid({ id: String(variantIdRaw) });
    if (!normalizedVariantId) {
      setCartHasUvInsert(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        // @ts-expect-error - shopUrl is set by the theme layout.
        const storeOrigin = window.shopUrl;
        if (!storeOrigin || typeof storeOrigin !== 'string') {
          if (!cancelled) {
            setCartHasUvInsert(false);
          }
          return;
        }

        const cart = await getCart({ storeOrigin });
        if (cancelled) {
          return;
        }

        const skip = checkCartForUvInsert({
          cart,
          variantId: normalizedVariantId,
          templateIds: resolvedUvInserts.templateIds
        });
        setCartHasUvInsert(skip);
      } catch (error) {
        if (!cancelled) {
          logError({
            label: 'GiftWrapContext',
            error,
            message: 'Failed to evaluate skipUvInsertsFlow from live cart',
            extraData: { variantId: normalizedVariantId }
          });
          setCartHasUvInsert(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [giftWrapModalOpen, activeProduct.id, resolvedUvInserts]);

  // No UV insert config is eligible for the wrapped product's segment. resolvedUvInserts
  // already has segment eligibility applied (ineligible configs are marked inactive), so
  // "configs exist but none active" means the inserts step has nothing to show.
  const noEligibleUvInserts = useMemo(() => {
    const configs = resolvedUvInserts?.configs ?? [];
    return configs.length > 0 && !configs.some((config) => config.active);
  }, [resolvedUvInserts]);

  // Skip the UV inserts step when the cart already holds an insert OR when no insert
  // config is eligible for this product's market segment.
  const skipUvInsertsFlow = useMemo(
    () => Boolean(cartHasUvInsert) || noEligibleUvInserts,
    [cartHasUvInsert, noEligibleUvInserts]
  );

  const contextValue = useMemo(
    () => ({
      loading,
      setLoading,
      addToCartLoading,
      setAddToCartLoading,
      giftWrapModalOpen,
      setGiftWrapModalOpen,
      priceModalOpen,
      setPriceModalOpen,
      bagInfoOpen,
      setBagInfoOpen,
      addBaseProduct,
      setAddBaseProduct,
      wrapOptions,
      setWrapOptions,
      personalisationChoices,
      setPersonalisationChoices,
      activeProduct,
      setActiveProduct,
      activePersonalisation,
      setActivePersonalisation,
      giftInfo,
      setGiftInfo,
      selectedItems,
      setSelectedItems,
      toggleGiftWrapSelection,
      isGiftWrapItemSelected,
      calculateGiftWrapTotal,
      calculateTotalPrice,
      updatePersonalisationChoice,
      getInputState,
      updateInputState,
      resetGiftWrapState,
      inputStates,
      // Visualiser export API
      registerVisualiser,
      unregisterVisualiser,
      getVisualiserExporters,
      exportAllVisualsAsBlobs,
      exportAllVisualsAsDataURLs,
      // Visualiser choices API
      visualiserChoices,
      getVisualiserChoice,
      setVisualiserChoice,
      resolvedUvInserts,
      skipUvInsertsFlow
    }),
    [
      loading,
      addToCartLoading,
      giftWrapModalOpen,
      priceModalOpen,
      bagInfoOpen,
      addBaseProduct,
      wrapOptions,
      personalisationChoices,
      activeProduct,
      activePersonalisation,
      giftInfo,
      selectedItems,
      toggleGiftWrapSelection,
      isGiftWrapItemSelected,
      calculateGiftWrapTotal,
      calculateTotalPrice,
      updatePersonalisationChoice,
      getInputState,
      updateInputState,
      resetGiftWrapState,
      inputStates,
      registerVisualiser,
      unregisterVisualiser,
      getVisualiserExporters,
      exportAllVisualsAsBlobs,
      exportAllVisualsAsDataURLs,
      visualiserChoices,
      getVisualiserChoice,
      setVisualiserChoice,
      resolvedUvInserts,
      skipUvInsertsFlow
    ]
  );

  return <GiftWrapContext.Provider value={contextValue}>{children}</GiftWrapContext.Provider>;
};

export const useGiftWrapContext = () => {
  const context = useContext(GiftWrapContext);
  if (context === undefined) {
    throw new Error('useGiftWrapContext must be used within GiftWrapProvider');
  }
  return context;
};
